import React, { useEffect, useMemo, useState } from "react";
import { DataGridPremium, GridColDef, GridToolbar, useGridApiRef } from "@mui/x-data-grid-premium";
import { Button, Chip, Tooltip } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import totalLocationData from "../../../../../../data/locationdata";
import { tableUseStyle } from "../../../etc/tableusestyle";
import { API, toastOptions, errorText, EXCLUDED_LOCATIONS, Position, mmdd, renderVideoConsentChip, videoConsentText, accountText, ResidentCell, AccountCell, isShownPosition } from "./rosterutils";
import ProfileEditModal from "./profileeditmodal";
import { StampButton, DateCell, SelectCell, TextCell, TodoFilterBar, fmtStamp, saveStamp, saveField, saveSexOffender, acqMode, hasOtherActiveAcquired, isOnboardingComplete, toastResult } from "./stafftodocommon";
import PositionEditModal from "./positioneditmodal";
import SendContractModal from "./sendcontractmodal";

//신규직원 TODO — 한 줄 = 직무(staffPositions, 활성만). 겸직이면 파트별로 줄이 따로
//입력형(파트·입사일·고보·비고) → /positions/field, 기록형 → /todo/stamp, 성범죄조회 → /todo/sexoffender(사람 단위)
//자동 항목(영상동의 여부·계약서 작성 확인)은 유캔싸인 상태. 필수 항목이 다 차면 "미완료"에서 빠지고 전체에서 연파랑
//[완료] = onboarding.closed 스탬프 → 미완료·전체에서 빠지고 "완료 처리" 토글에서만 보임(완료 취소 가능). [삭제] = 계약서 이력 없는 직무만 지움
const CANCEL_MESSAGE = "관리자에 의해 취소되었습니다";
const INSURANCE_OPTIONS = [{ value: "O", label: "O" }, { value: "X", label: "X" }, { value: "해당X", label: "해당X" }];

type Row = Position & { _siblings: Position[]; _acqMode: "na" | "naMulti" | "input"; _otherAcquired: boolean; _complete: boolean };

//서버 응답에 onboarding/offboarding 이 빠져 있어도(3단계 이전 서버) 빈 객체로 — valueGetter 에서 undefined 접근 방지
const normalize = (list: any[]): Position[] => (list || []).map((p: any) => ({ ...p, onboarding: p.onboarding || {}, offboarding: p.offboarding || {}, isManaged: p.isManaged === undefined ? 1 : p.isManaged, isHidden: p.isHidden ? 1 : 0 }));

const StaffOnboarding: React.FC<any> = (props) => {

    const classes = tableUseStyle();
    const apiRef = useGridApiRef();

    const [positions, setPositions] = useState<Position[]>([]);
    const [parts, setParts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<"incomplete" | "all" | "closed">("incomplete");
    const [branch, setBranch] = useState("all");
    const [search, setSearch] = useState<string>(props.initialSearch || "");
    const [busyId, setBusyId] = useState<number | null>(null);
    const [sendTarget, setSendTarget] = useState<number | null>(null);
    const [editModal, setEditModal] = useState<{ open: boolean; userId: number; userName: string }>({ open: false, userId: 0, userName: "" });
    const [profileEdit, setProfileEdit] = useState<{ open: boolean; row: Position | null }>({ open: false, row: null });

    const branchOptions = totalLocationData.filter((each) =>
        props.user && each.academy.includes(props.user.academy) && !EXCLUDED_LOCATIONS.includes(each.english));

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        try {
            setLoading(true);
            const [r, p]: any[] = await Promise.all([getFetch(`${API}/roster`), getFetch(`${API}/parts`)]);
            if (r.message === "success") setPositions(normalize(r.positions));
            else toast.error(errorText(r, "목록을 불러오지 못했습니다"), toastOptions);
            if (p.message === "success") setParts(p.parts || []);
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    //변경 후 조용히 재조회 (스피너 없이)
    const refresh = async () => {
        try {
            const r: any = await getFetch(`${API}/roster`);
            if (r.message === "success") setPositions(normalize(r.positions));
        } catch (e) {
            console.log(e);
        }
    };

    //행 파생값: 형제 직무·취득신고 모드·완료 여부
    const rows: Row[] = useMemo(() => {
        const byUser: { [userId: number]: Position[] } = {};
        positions.forEach((p) => { (byUser[p.userId] = byUser[p.userId] || []).push(p); });
        return positions.filter((p) => p.isActive === 1 && isShownPosition(p)).map((p) => {
            const siblings = (byUser[p.userId] || []).filter((s) => s.id !== p.id);
            return { ...p, _siblings: siblings, _acqMode: acqMode(p, siblings), _otherAcquired: hasOtherActiveAcquired(siblings), _complete: isOnboardingComplete(p, siblings) };
        });
    }, [positions]);

    const visibleRows = useMemo(() => {
        const text = search.trim();
        return rows.filter((r) => {
            const closed = !!(r.onboarding && r.onboarding.closed);
            if (status === "closed") { if (!closed) return false; }
            else if (closed) return false;
            if (status === "incomplete" && r._complete) return false;
            if (branch !== "all" && r.branchKey !== branch) return false;
            if (text && !String(r.name || "").includes(text)) return false;
            return true;
        });
    }, [rows, status, branch, search]);

    //===== 저장 액션 =====
    const field = async (row: Row, name: string, value: any) => {
        const r = await saveField(row.id, name, value);
        if (toastResult(r, null, "저장에 실패했습니다")) await refresh();
    };
    const stamp = async (row: Row, key: string, value: any) => {
        const r = await saveStamp(row.id, "onboarding", key, value);
        if (toastResult(r, null, "기록에 실패했습니다")) await refresh();
    };
    const sexOffender = async (row: Row, checked: boolean) => {
        const r = await saveSexOffender(row.userId, checked);
        if (toastResult(r, null, "기록에 실패했습니다")) await refresh();
    };

    //첨부 OCR 다시 읽기 (needsReview 건) — 빈 값은 채우고 있는 값은 비교만
    const reocr = async (row: Position) => {
        if (!window.confirm(`${row.name}의 계약서 첨부(신분증·통장사본)를 다시 읽을까요? 이미 있는 값은 덮어쓰지 않습니다.`)) return;
        try {
            setBusyId(row.id);
            const result: any = await postFetch(`${API}/profiles/reocr`, { userId: row.userId });
            if (result.message === "success") {
                const pf = result.profile || {};
                toast[pf.needsReview ? "error" : "success"](pf.needsReview ? `다시 읽었지만 확인 필요: ${pf.reviewMemo || ""}` : "주민번호·계좌를 읽어왔습니다", toastOptions);
                await refresh();
            } else {
                toast.error(result.message === "noFirstContract" ? "서명완료된 계약서가 없습니다" : errorText(result, "다시 읽기에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setBusyId(null);
        }
    };

    //완료 처리: closed 스탬프. 항목이 덜 찼으면 한 번 더 확인
    const closeRow = async (row: Row) => {
        const msg = row._complete
            ? `${row.name} 신규직원 TODO를 완료 처리할까요? 목록에서 빠지고 "완료 처리" 보기에서 되돌릴 수 있습니다.`
            : `${row.name}은(는) 아직 안 채워진 항목이 있습니다. 그래도 완료 처리할까요? (목록에서 빠지고 "완료 처리" 보기에서 되돌릴 수 있습니다)`;
        if (!window.confirm(msg)) return;
        const r = await saveStamp(row.id, "onboarding", "closed", true);
        if (toastResult(r, "완료 처리했습니다", "완료 처리에 실패했습니다")) await refresh();
    };
    const reopenRow = async (row: Row) => {
        if (!window.confirm(`${row.name} 완료 처리를 취소하고 다시 목록에 보일까요?`)) return;
        const r = await saveStamp(row.id, "onboarding", "closed", null);
        if (toastResult(r, "완료 처리를 취소했습니다", "취소에 실패했습니다")) await refresh();
    };
    //삭제: 계약서 이력이 없는 직무만 (있으면 서버가 hasDocuments 로 거부 → 퇴사직원 TODO 직무해제 안내)
    const deleteRow = async (row: Row) => {
        if (!window.confirm(`${row.name}의 ${row.partName || "(파트 없음)"} 직무를 삭제할까요?\n직무·개인 변수 기록이 지워지고 되돌릴 수 없습니다. 다른 직무가 없으면 직원명부에서도 빠집니다.`)) return;
        try {
            setBusyId(row.id);
            const r: any = await postFetch(`${API}/positions/delete`, { positionId: row.id });
            if (r.message === "success") {
                toast.success(r.remaining === 0 ? "직무를 삭제했고 직원명부에서도 뺐습니다" : "직무를 삭제했습니다", toastOptions);
                await refresh();
            } else if (r.message === "hasDocuments") {
                toast.error(`계약서 이력이 ${r.count}건 있어 삭제할 수 없습니다. 퇴사직원 TODO에서 직무해제 하세요.`, toastOptions);
            } else {
                toast.error(errorText(r, "삭제에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setBusyId(null);
        }
    };

    const sendVideoConsent = async (row: Row) => {
        if (!window.confirm(`${row.name}에게 영상정보 동의서를 카카오로 보낼까요?`)) return;
        try {
            setBusyId(row.id);
            const result: any = await postFetch(`${API}/videoconsent/send`, { userIds: [row.userId] });
            if (result.message === "success") {
                const one = (result.results || [])[0];
                if (one && one.ok) toast.success("영상동의서를 발송했습니다", toastOptions);
                else toast.error(`발송 실패: ${one ? one.error : ""}`, toastOptions);
                await refresh();
            } else {
                toast.error(errorText(result, "발송에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setBusyId(null);
        }
    };

    const remind = async (row: Row, docRowId: number, expired: boolean) => {
        if (!window.confirm(`${row.name}에게 서명 재알림을 보낼까요?${expired ? " (유효기간이 지나 7일 연장 후 보냅니다)" : ""}`)) return;
        try {
            setBusyId(row.id);
            const result: any = await postFetch(`${API}/documents/remind`, { id: docRowId });
            if (result.message === "success") toast.success(`재알림 발송 (${result.remindCount}회${result.extended ? ", 유효기간 +7일" : ""})`, toastOptions);
            else toast.error(errorText(result, "재알림에 실패했습니다"), toastOptions);
            await refresh();
        } catch (e) {
            console.log(e);
        } finally {
            setBusyId(null);
        }
    };

    const cancel = async (row: Row, docRowId: number, what: string) => {
        if (!window.confirm(`${row.name}의 ${what} 서명 요청을 취소할까요?`)) return;
        try {
            setBusyId(row.id);
            const result: any = await postFetch(`${API}/documents/cancel`, { id: docRowId, message: CANCEL_MESSAGE });
            if (result.message === "success") toast.success("요청을 취소했습니다", toastOptions);
            else toast.error(errorText(result, "취소에 실패했습니다"), toastOptions);
            await refresh();
        } catch (e) {
            console.log(e);
        } finally {
            setBusyId(null);
        }
    };

    const naText = (mode: "na" | "naMulti" | "input") => (mode === "na" ? "해당없음" : mode === "naMulti" ? "해당없음(겸직)" : "");
    const sendDisabledReason = (row: Row) => (!row.partId || !row.startDate ? "파트·입사일을 먼저 입력하세요" : "");

    const remindCancel = (row: Row, docRowId: number, expired: boolean, what: string) => (
        <span style={{ display: "inline-flex", gap: "4px", marginLeft: "6px" }}>
            <Button size="sm" variant="outlined" color={expired ? "warning" : "primary"} loading={busyId === row.id} onClick={() => remind(row, docRowId, expired)}>{expired ? "재알림 (+7일)" : "재알림"}</Button>
            <Button size="sm" variant="outlined" color="danger" disabled={busyId === row.id} onClick={() => cancel(row, docRowId, what)}>취소</Button>
        </span>
    );

    const columns: GridColDef[] = useMemo(() => [
        {
            field: "name", headerName: "이름", width: 110,
            renderCell: (params: any) => <span style={{ fontFamily: "Apple_SB" }}>{params.row.name}{params.row.isMultiple && <Chip size="sm" variant="soft" color="primary" sx={{ marginLeft: "4px" }}>겸직</Chip>}</span>
        },
        {
            field: "branchLabel", headerName: "지점", width: 130, valueGetter: (params: any) => params.row.branchLabel || "",
            //직무 지점은 학생정보 지점과 별개 컬럼 — 비어 있으면(운영진 등) 여기서 직접 고름
            renderCell: (params: any) => (
                <SelectCell value={params.row.branchKey || ""} width={120} placeholder="지점 선택"
                    options={branchOptions.map((b) => ({ value: b.english, label: b.korean }))}
                    onCommit={(v) => field(params.row, "branchKey", v)} />
            )
        },
        {
            field: "partId", headerName: "파트", width: 200, valueGetter: (params: any) => params.row.partName || "",
            renderCell: (params: any) => (
                <SelectCell value={params.row.partId ? String(params.row.partId) : ""} width={190} placeholder="파트 선택"
                    options={parts.map((p) => ({ value: String(p.id), label: p.name }))}
                    onCommit={(v) => field(params.row, "partId", v ? +v : null)} />
            )
        },
        {
            field: "startDate", headerName: "입사일", width: 150, valueGetter: (params: any) => params.row.startDate || "",
            renderCell: (params: any) => <DateCell value={params.row.startDate || ""} onCommit={(v) => field(params.row, "startDate", v)} />
        },
        { field: "workDuration", headerName: "근무기간", width: 100 },
        {
            field: "employmentInsurance", headerName: "고보 적용", width: 110, valueGetter: (params: any) => params.row.employmentInsurance || "",
            renderCell: (params: any) => <SelectCell value={params.row.employmentInsurance || ""} width={95} options={INSURANCE_OPTIONS} onCommit={(v) => field(params.row, "employmentInsurance", v)} />
        },
        {
            field: "memo", headerName: "비고", width: 160,
            renderCell: (params: any) => <TextCell value={params.row.memo || ""} width={150} onCommit={(v) => field(params.row, "memo", v)} />
        },
        //주민번호·계좌 (사람 단위) — 사진 보기 + 연필로 직접 수정. 명부에서는 보기만
        {
            field: "residentNumber", headerName: "주민등록번호", width: 280, valueGetter: (params: any) => params.row.residentNumber || "",
            renderCell: (params: any) => <ResidentCell row={params.row} busy={busyId === params.row.id} onReocr={reocr} onEdit={(row) => setProfileEdit({ open: true, row })} />
        },
        {
            field: "bankAccount", headerName: "계좌", width: 270, valueGetter: (params: any) => accountText(params.row),
            renderCell: (params: any) => <AccountCell row={params.row} onEdit={(row) => setProfileEdit({ open: true, row })} />
        },
        {
            field: "acquisitionInput", headerName: "취득신고 입력", width: 200,
            valueGetter: (params: any) => (params.row._acqMode !== "input" ? naText(params.row._acqMode) : (params.row.onboarding.acquisitionInput ? params.row.onboarding.acquisitionInput.value : "")),
            renderCell: (params: any) => <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}><DateCell value={params.row.onboarding.acquisitionInput ? params.row.onboarding.acquisitionInput.value : ""} naText={naText(params.row._acqMode)} onCommit={(v) => stamp(params.row, "acquisitionInput", v)} />{params.row._acqMode === "input" && params.row._otherAcquired && !params.row.onboarding.acquisitionInput && <span style={{ fontSize: "10px", color: "#9ca3af", whiteSpace: "nowrap" }}>겸직 — 해당 시</span>}</span>
        },
        {
            field: "acquisitionDone", headerName: "취득신고 이행", width: 200,
            valueGetter: (params: any) => (params.row._acqMode !== "input" ? naText(params.row._acqMode) : (params.row.onboarding.acquisitionDone ? params.row.onboarding.acquisitionDone.value : "")),
            renderCell: (params: any) => <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}><DateCell value={params.row.onboarding.acquisitionDone ? params.row.onboarding.acquisitionDone.value : ""} naText={naText(params.row._acqMode)} onCommit={(v) => stamp(params.row, "acquisitionDone", v)} />{params.row._acqMode === "input" && params.row._otherAcquired && !params.row.onboarding.acquisitionDone && <span style={{ fontSize: "10px", color: "#9ca3af", whiteSpace: "nowrap" }}>겸직 — 해당 시</span>}</span>
        },
        {
            field: "videoSend", headerName: "영상동의서 발송", width: 150,
            valueGetter: (params: any) => (params.row.videoConsentDoc && params.row.videoConsentDoc.status !== "canceled" ? `${params.row.videoConsentDoc.sentByName} · ${mmdd(params.row.videoConsentDoc.sentAt)}` : ""),
            renderCell: (params: any) => {
                const row: Row = params.row;
                const doc = row.videoConsentDoc;
                if (doc && doc.status !== "canceled") {
                    return <Button size="sm" variant="soft" color="success" sx={{ cursor: "default", fontSize: "12px" }}>{doc.sentByName} · {mmdd(doc.sentAt)}</Button>;
                }
                const reason = sendDisabledReason(row);
                const btn = <span><Button size="sm" variant="soft" color="neutral" disabled={!!reason} loading={busyId === row.id} onClick={() => sendVideoConsent(row)}>발송</Button></span>;
                return reason ? <Tooltip title={reason} placement="top">{btn}</Tooltip> : btn;
            }
        },
        {
            field: "videoConsentStatus", headerName: "영상조회 동의여부 / 취소", width: 260,
            valueGetter: (params: any) => videoConsentText(params.row.videoConsentStatus),
            renderCell: (params: any) => {
                const row: Row = params.row;
                return (
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                        {renderVideoConsentChip(row.videoConsentStatus)}
                        {row.videoConsentStatus === "pending" && row.videoConsentDoc && remindCancel(row, row.videoConsentDoc.id, row.videoConsentDoc.expired, "영상동의서")}
                    </span>
                );
            }
        },
        {
            field: "contractSend", headerName: "근로계약서 발송", width: 150,
            valueGetter: (params: any) => (params.row.latestDoc && params.row.latestDoc.status !== "canceled" ? `${params.row.latestDoc.sentByName} · ${mmdd(params.row.latestDoc.sentAt)}` : ""),
            renderCell: (params: any) => {
                const row: Row = params.row;
                const doc = row.latestDoc;
                if (doc && doc.status !== "canceled") {
                    return <Button size="sm" variant="soft" color="success" sx={{ cursor: "default", fontSize: "12px" }}>{doc.sentByName} · {mmdd(doc.sentAt)}</Button>;
                }
                const reason = sendDisabledReason(row);
                const btn = <span><Button size="sm" variant="soft" color="neutral" disabled={!!reason} onClick={() => setSendTarget(row.id)}>발송</Button></span>;
                return reason ? <Tooltip title={reason} placement="top">{btn}</Tooltip> : btn;
            }
        },
        {
            field: "contractCheck", headerName: "계약서 작성 확인 / 취소", width: 260,
            valueGetter: (params: any) => (!params.row.isFirst ? `서명완료 ${mmdd(params.row.firstCompletedAt)}` : params.row.contractState === "pending" ? "미서명" : params.row.contractState === "canceled" ? "요청취소" : "미발송"),
            renderCell: (params: any) => {
                const row: Row = params.row;
                if (!row.isFirst) {
                    return <span><Chip size="sm" variant="soft" color="success">서명완료</Chip><span style={{ marginLeft: "6px", fontSize: "11px", color: "#6b7280" }}>{mmdd(row.firstCompletedAt)}</span></span>;
                }
                if (row.contractState === "pending" && row.latestDoc) {
                    return <span style={{ display: "inline-flex", alignItems: "center" }}><Chip size="sm" variant="soft" color="warning">미서명</Chip>{remindCancel(row, row.latestDoc.id, row.expired, "근로계약서")}</span>;
                }
                if (row.contractState === "canceled") {
                    return <Chip size="sm" variant="soft" color="danger">요청취소</Chip>;
                }
                return <Chip size="sm" variant="soft" color="neutral">미발송</Chip>;
            }
        },
        {
            field: "kakaoSent", headerName: "카톡메세지 발송", width: 150,
            valueGetter: (params: any) => fmtStamp(params.row.onboarding.kakaoSent),
            renderCell: (params: any) => <StampButton stamp={params.row.onboarding.kakaoSent} label="보냈음" onSet={() => stamp(params.row, "kakaoSent", true)} onClear={() => stamp(params.row, "kakaoSent", null)} />
        },
        {
            field: "kakaoRead", headerName: "카톡 읽음확인", width: 150,
            valueGetter: (params: any) => fmtStamp(params.row.onboarding.kakaoRead),
            renderCell: (params: any) => <StampButton stamp={params.row.onboarding.kakaoRead} label="읽음" onSet={() => stamp(params.row, "kakaoRead", true)} onClear={() => stamp(params.row, "kakaoRead", null)} />
        },
        {
            field: "sexOffender", headerName: "성범죄조회 확인", width: 150,
            valueGetter: (params: any) => (params.row.sexOffender ? `${params.row.sexOffender.checkedByName} · ${mmdd(params.row.sexOffender.checkedAt)}` : ""),
            renderCell: (params: any) => {
                const so = params.row.sexOffender;
                return <StampButton stamp={so ? { value: true, doneBy: so.checkedBy, doneByName: so.checkedByName, doneAt: so.checkedAt } : null} label="확인"
                    onSet={() => sexOffender(params.row, true)} onClear={() => sexOffender(params.row, false)} />;
            }
        },
        {
            field: "actions", headerName: "", width: 230, sortable: false, filterable: false, disableExport: true,
            renderCell: (params: any) => {
                const row: Row = params.row;
                const closed = !!(row.onboarding && row.onboarding.closed);
                return (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
                        {closed
                            ? <Tooltip title={`완료 처리 ${fmtStamp(row.onboarding.closed)}`} placement="top"><Button size="sm" variant="soft" color="neutral" disabled={busyId === row.id} onClick={() => reopenRow(row)}>완료 취소</Button></Tooltip>
                            : <Button size="sm" variant="soft" color={row._complete ? "primary" : "neutral"} disabled={busyId === row.id} onClick={() => closeRow(row)}>완료</Button>}
                        <Button size="sm" variant="plain" color="danger" disabled={busyId === row.id} onClick={() => deleteRow(row)}>삭제</Button>
                        <Button size="sm" variant="plain" color="neutral" onClick={() => setEditModal({ open: true, userId: row.userId, userName: row.name })}>파트 추가</Button>
                    </span>
                );
            }
        }
    ], [parts, busyId]);

    return (
        <div>
            <TodoFilterBar status={status} onStatus={setStatus} showClosed branch={branch} onBranch={setBranch} branchOptions={branchOptions} search={search} onSearch={setSearch}
                right={<span style={{ fontFamily: "Apple_R", fontSize: "12px", color: "#9ca3af" }}>학생정보 → 전체학생정보에서 직원 줄 선택 후 "신규TODO" 누르면 이 화면으로 옴 · 표시 {visibleRows.length}줄</span>} />

            <div className={classes.root} style={{ height: "680px", width: "100%", backgroundColor: "white" }}>
                <DataGridPremium
                    rows={visibleRows}
                    columns={columns}
                    density="compact"
                    loading={loading}
                    apiRef={apiRef}
                    disableRowSelectionOnClick
                    getRowClassName={(params) => (params.row._complete ? "super-app-theme--처리완료" : "")}
                    initialState={{ pinnedColumns: { left: ["name"] } }}
                    slots={{ toolbar: GridToolbar }}
                    getRowHeight={() => 46}
                />
            </div>

            <div style={{ marginTop: "12px", padding: "12px 16px", background: "#fffbea", border: "1px solid #f5e6a8", borderRadius: "8px", fontFamily: "Apple_R", fontSize: "12px", color: "#6b7280", lineHeight: 1.7 }}>
                <b>버튼 종류</b> · <b>발송</b>(영상동의서·근로계약서) = 누르면 유캔싸인으로 진짜 발송되고 버튼이 "누른사람 · 날짜"로 바뀜 · <b>보냈음 / 읽음 / 확인</b>(카톡·성범죄조회) = 내가 직접 한 뒤 누르는 기록용(다시 누르면 해제) ·
                <b> 동의여부 / 작성 확인</b> = 직원이 서명하면 자동으로 바뀜, 서명 전엔 재알림·취소 가능 · <b>고보 적용</b> 등 선택칸은 처음엔 비어 있음 · 고보가 X/해당X면 취득신고는 해당없음, 겸직(다른 파트 재직 중·이미 이행)도 이 파트 취득신고를 했으면 입력(완료 판정에는 필수 아님) · 모든 항목이 채워지면 "미완료" 목록에서 빠짐 · <b>주민번호·계좌</b>는 계약서 첨부에서 자동으로 읽고(한 번 읽으면 끝) 틀리면 연필로 직접 수정 · <b>완료</b> = 이 TODO를 닫음(전체에서도 빠지고 "완료 처리" 보기에서 되돌림) · <b>삭제</b> = 잘못 만든 직무 삭제(계약서 이력이 있으면 불가 → 퇴사직원 TODO의 직무해제)
            </div>

            <PositionEditModal
                open={editModal.open}
                mode="create"
                position={null}
                userId={editModal.userId}
                userName={editModal.userName}
                branchOptions={branchOptions}
                parts={parts}
                onClose={() => setEditModal((prev) => ({ ...prev, open: false }))}
                onSaved={refresh}
            />

            <SendContractModal
                open={sendTarget !== null}
                positionIds={sendTarget !== null ? [sendTarget] : []}
                user={props.user}
                onClose={(sent: boolean) => { setSendTarget(null); if (sent) refresh(); }}
            />

            <ProfileEditModal
                open={profileEdit.open}
                userId={profileEdit.row ? profileEdit.row.userId : 0}
                userName={profileEdit.row ? profileEdit.row.name : ""}
                initial={profileEdit.row ? { residentNumber: profileEdit.row.residentNumber, bankName: profileEdit.row.bankName, bankAccount: profileEdit.row.bankAccount, accountHolder: profileEdit.row.accountHolder } : null}
                onClose={() => setProfileEdit((prev) => ({ ...prev, open: false }))}
                onSaved={refresh}
            />
        </div>
    );
};

export default StaffOnboarding;
